// src/features/sheets/components/SheetSelector.tsx
// Reusable dropdown component for selecting/linking sheets

import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  Link2,
  Link2Off,
  Plus,
  ExternalLink,
  FileSpreadsheet,
} from "lucide-react";
import { useSheetsStore, useSheetById } from "../store/sheetsStore";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SheetSelectorProps {
  /** Currently linked sheet ID (if any) */
  linkedSheetId?: string | undefined;
  /** Called when user selects a different sheet (null = local/unlink) */
  onSelectSheet: (sheetId: string | null) => void;
  /** Called when user wants to create a new sheet */
  onCreateNewSheet: () => void;
  /** Called when user wants to open the sheet in full page view */
  onOpenInFullPage?: (() => void) | undefined;
  /** Whether to show the "Open in Sheets Page" option */
  showOpenInFullPage?: boolean | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const SheetSelector: React.FC<SheetSelectorProps> = ({
  linkedSheetId,
  onSelectSheet,
  onCreateNewSheet,
  onOpenInFullPage,
  showOpenInFullPage = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const sheets = useSheetsStore((s) => s.sheets);
  const linkedSheet = useSheetById(linkedSheetId ?? null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded transition-colors ${
          linkedSheetId
            ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
            : "bg-slate-700/50 text-slate-400 hover:bg-slate-700"
        }`}
        title={linkedSheetId ? `Linked to: ${linkedSheet?.name}` : "Link to sheet"}
      >
        {linkedSheetId ? (
          <Link2 className="w-3 h-3" />
        ) : (
          <Link2Off className="w-3 h-3" />
        )}
        <span className="max-w-[80px] truncate">
          {linkedSheet?.name || "Local"}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 bg-slate-800/80 border-b border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">
              Sheet Source
            </div>
          </div>

          {/* Local option */}
          <button
            onClick={() => {
              onSelectSheet(null);
              setIsOpen(false);
            }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-700/50 ${
              !linkedSheetId ? "bg-slate-700/30 text-white" : "text-slate-300"
            }`}
          >
            <Link2Off className="w-3.5 h-3.5 text-slate-500" />
            <div className="flex-1 text-left">
              <div className="font-medium">Local Data</div>
              <div className="text-[10px] text-slate-500">
                Stored in widget only
              </div>
            </div>
          </button>

          {/* Divider */}
          {sheets.length > 0 && (
            <div className="border-t border-slate-700 my-1" />
          )}

          {/* Existing sheets */}
          {sheets.length > 0 && (
            <div className="max-h-[200px] overflow-y-auto">
              <div className="px-3 py-1 text-[10px] text-slate-500 uppercase tracking-wide">
                Your Sheets
              </div>
              {sheets.map((sheet) => (
                <button
                  key={sheet.id}
                  onClick={() => {
                    onSelectSheet(sheet.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-700/50 ${
                    linkedSheetId === sheet.id
                      ? "bg-blue-500/20 text-blue-300"
                      : "text-slate-300"
                  }`}
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium truncate">{sheet.name}</div>
                    <div className="text-[10px] text-slate-500">
                      {sheet.rows} × {sheet.cols} •{" "}
                      {new Date(sheet.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                  {linkedSheetId === sheet.id && (
                    <Link2 className="w-3 h-3 text-blue-400" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-slate-700">
            <button
              onClick={() => {
                onCreateNewSheet();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-400 hover:bg-slate-700/50"
            >
              <Plus className="w-3.5 h-3.5" />
              Create New Sheet
            </button>
            {showOpenInFullPage && linkedSheetId && onOpenInFullPage && (
              <button
                onClick={() => {
                  onOpenInFullPage();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-slate-700/50"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open in Sheets Page
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SheetSelector;
